<script setup lang="ts">
// 组件卸载时清理定时器
import { computed, onBeforeUnmount, ref, watch } from "vue";

const properties = withDefaults(
  defineProps<{
    open: boolean;
    title?: string;
    message: string;
    confirmText?: string;
    position?: "center" | "bottom";
    duration?: number;
    color?: "primary" | "success" | "warning" | "error" | "neutral";
  }>(),
  {
    title: "",
    confirmText: "",
    position: "center",
    duration: 3000,
    color: "primary",
  },
);

const emit = defineEmits<{ "update:open": [value: boolean] }>();

const isOpen = computed({
  get: () => properties.open,
  set: (v) => emit("update:open", v),
});
// 底部通知自动消失逻辑
const showBottom = ref(properties.open && properties.position === "bottom");
let timer: ReturnType<typeof setTimeout> | null = null;

function clearTimer() {
  if (!timer) {
    return;
  }

  clearTimeout(timer);
  timer = null;
}

watch(
  () => properties.open,
  (newValue: boolean) => {
    if (properties.position === "bottom") {
      if (newValue) {
        showBottom.value = true;
        clearTimer();
        timer = setTimeout(() => {
          emit("update:open", false);
        }, properties.duration);
      } else {
        showBottom.value = false;
        clearTimer();
      }
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => clearTimer());
</script>

<template>
  <UModal
    v-if="position === 'center'"
    v-model:open="isOpen"
    :ui="{
      content: 'max-w-md',
      header: 'px-5 py-4 border-b border-default',
      body: 'px-5 py-6',
      footer: 'px-5 py-3 border-t border-default flex items-center justify-center',
      title: 'text-base font-semibold text-highlighted',
    }">
    <template #header>
      <span
        v-if="title"
        class="text-base font-semibold text-highlighted">
        {{ title }}
      </span>
    </template>
    <template
      v-if="message"
      #body>
      <div class="text-center">
        <p class="text-default">{{ message }}</p>
      </div>
    </template>
    <template #footer>
      <UButton
        variant="solid"
        :color="color"
        size="sm"
        @click="void (isOpen = false)">
        {{ confirmText || "确定" }}
      </UButton>
    </template>
  </UModal>
  <!-- 底部通知条 -->
  <div
    v-if="position === 'bottom' && showBottom"
    class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
    <div
      class="bg-default shadow-lg rounded-lg p-4 border border-default flex items-center justify-between">
      <div class="flex-1 min-w-0">
        <p
          v-if="title"
          class="text-sm font-semibold text-highlighted truncate">
          {{ title }}
        </p>
        <p class="text-sm text-default mt-0.5">{{ message }}</p>
      </div>
      <UButton
        variant="ghost"
        color="neutral"
        size="xs"
        icon="i-heroicons-x-mark"
        class="shrink-0 ml-3"
        @click="emit('update:open', false)" />
    </div>
  </div>
</template>
